import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { RootState } from 'app/AppStore';
import BatteryInfoCard from 'components/BatteryInfo';
import { getFiltersMap } from 'components/Filter/Filter.types';
import FilterQuantity from 'components/Filter/FilterQuantity';
import FilterTags from 'components/Filter/FilterTags';
import {
  getIamCatalogFilters,
  getLastSearchedVehicleKey,
  setIamRangeFilter,
  setIamTextFilter,
} from 'domains/catalog/Catalog.store';
import { IAMLaborTimesNodeWrapper, IAMReferencesWrapper, OTHER_BRAND_KEY } from 'domains/catalog/Catalog.types';
import { getIAMReferences } from 'domains/references';
import { getSparePartsView } from 'domains/user';
import { getIamBatteryPathname } from 'pages/CatalogPage/common/QuickAccess/useQuickAccessLinks';
import SparePartsBrandTab from 'pages/CatalogPage/IAM/SubcategorySection/SparePartsSection/SparePartsBrandTab';
import { Flex, MarginBox } from 'UI';
import ViewTabs from 'UI/Tabs/ViewTabs';
import { getData, NO_DATA } from 'utils';
import { useResetScroll } from 'utils/hooks/useResetScroll';

export interface OtherBrandSectionProps {
  referencesWrapper: IAMReferencesWrapper;
  nodeId?: string;
  laborTimesWrapper: NO_DATA | IAMLaborTimesNodeWrapper;
}

const OtherBrandSection = ({ referencesWrapper, nodeId, laborTimesWrapper }: OtherBrandSectionProps) => {
  const dispatch = useDispatch();
  const filters = useSelector(getIamCatalogFilters);
  const sparePartsView = useSelector(getSparePartsView);
  const [numOfProducts, setNumOfProducts] = useState<number>(0);
  const referencesWrapperData = getData(referencesWrapper);
  const referenceNumbers = useMemo(() => referencesWrapperData?.referenceNumbers ?? [], [referencesWrapperData]);
  const vehicleKey = useSelector(getLastSearchedVehicleKey) ?? '';
  const iamReferencesPayload = useMemo(() => ({ vehicleKey, referenceNumbers }), [vehicleKey, referenceNumbers]);
  const references = useSelector((state: RootState) => getIAMReferences(state, iamReferencesPayload));
  const { pathname } = useLocation();
  const iamBatteryPathname = getIamBatteryPathname();
  const isBatteryUrl = pathname.includes(iamBatteryPathname);
  const isCompatibleBatteries = useMemo(() => references.some((r) => r.isBatteryCompatibleWithVehicle), [references]);

  useResetScroll();

  return (
    <>
      <FilterTags
        filters={filters.otherBrandFilters}
        setTextFilters={(id, item) => dispatch(setIamTextFilter({ id, item, filterType: OTHER_BRAND_KEY }))}
        setRangeFilters={(id, range) => dispatch(setIamRangeFilter({ id, range, filterType: OTHER_BRAND_KEY }))}
      />
      <MarginBox my={15} />
      <MarginBox mb={-30}>
        <FilterQuantity numberOfProducts={numOfProducts} />
      </MarginBox>
      <Flex direction={'column'}>
        <ViewTabs />
        <SparePartsBrandTab
          sparePartsView={sparePartsView}
          referencesWrapper={referencesWrapper}
          nodeId={nodeId}
          laborTimesWrapper={laborTimesWrapper}
          setNumOfProducts={setNumOfProducts}
          isCompatible={isBatteryUrl && isCompatibleBatteries}
          filterType={OTHER_BRAND_KEY}
          facetsMapBuilder={getFiltersMap}
          leftTopContent={<BatteryInfoCard />}
          withTopMargin
        />
      </Flex>
    </>
  );
};

export default OtherBrandSection;
